import {
  Autocomplete,
  Button,
  type Item,
  SelectInput,
  SelectInputItem,
  TextField,
} from '@ads/components-react';
import {
  AdjustmentsHorizontalIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DatePicker } from '@/shared/ui/atoms';
import { getAutocompleteSearchMessage } from '@/shared/utils/getAutocompleteSearchMessage';
import { useCompaniesDropdown } from '../../../hooks/useCompaniesDropdown';
import { useCoursesDropdown } from '../../../hooks/useCoursesDropdown';
import { useEnrollmentFilters } from '../../../hooks/useEnrollmentList';
import { usePolosDropdown } from '../../../hooks/usePolosDropdown';
import { useStudentSearchDropdown } from '../../../hooks/useStudentSearchDropdown';
import type { EnrollmentStatus } from '../../../types/enrollment';

const ALL_STATUSES: EnrollmentStatus[] = [
  'payment_selection',
  'payment_pending',
  'approval_pending',
  'approved',
  'activating',
  'active',
  'transferred',
  'transferred_in_registration',
  'canceled',
  'canceled_in_registration',
  'rejected',
  'finished_with_pendencies',
  'finished',
  'finished_with_monograph',
  'finished_with_certificate',
  'blocked',
];

export interface EnrollmentFilterBarProps {
  readonly isLoading?: boolean;
}

export function EnrollmentFilterBar({ isLoading = false }: EnrollmentFilterBarProps) {
  const { t } = useTranslation('personManagement');
  const { t: tCommon } = useTranslation();
  const { filters, updateFilters, applyFilters, resetFilters, canApply } = useEnrollmentFilters();
  const [showAdvanced, _setShowAdvanced] = useState(false);
  const [datePickerKey, setDatePickerKey] = useState(0);
  const [studentSearch, setStudentSearch] = useState('');
  const [courseSearch, setCourseSearch] = useState('');
  const [companySearch, setCompanySearch] = useState('');
  const [selectedCompanyLabel, setSelectedCompanyLabel] = useState('');
  const [selectedCourseLabel, setSelectedCourseLabel] = useState('');
  const [selectedStudentLabel, setSelectedStudentLabel] = useState('');
  const { students, isQuerySuccess: isStudentQuerySuccess } =
    useStudentSearchDropdown(studentSearch);
  const { companies, isQuerySuccess: isCompanyQuerySuccess } = useCompaniesDropdown(companySearch);
  const { courses, isQuerySuccess: isCourseQuerySuccess } = useCoursesDropdown(courseSearch);
  const { polos } = usePolosDropdown();

  const companyItems = companies.map((c): Item<string> => ({ label: c.name, value: c.id }));
  const selectedCompany: Item<string> = filters.company
    ? { label: selectedCompanyLabel, value: filters.company }
    : { label: '', value: '' };

  const courseItems = courses.map((c): Item<string> => ({ label: c.name, value: c.id }));
  const selectedCourse: Item<string> = filters.courseId
    ? { label: selectedCourseLabel, value: filters.courseId }
    : { label: '', value: '' };

  const studentItems = students.map(
    (s): Item<string> => ({
      label: s.value ?? '',
      value: s.key ?? '',
    }),
  );
  const selectedStudent: Item<string> | undefined = filters.studentName
    ? { label: selectedStudentLabel, value: filters.studentName }
    : undefined;
  const studentMessage = getAutocompleteSearchMessage(
    studentSearch.length,
    isStudentQuerySuccess,
    studentItems.length,
    {
      minChars: tCommon('search.minChars'),
      searching: tCommon('search.searching'),
      notFound: tCommon('search.notFound'),
    },
  );

  const companyMessage = getAutocompleteSearchMessage(
    companySearch.length,
    isCompanyQuerySuccess,
    companyItems.length,
    {
      minChars: tCommon('search.minChars'),
      searching: tCommon('search.searching'),
      notFound: tCommon('search.notFound'),
    },
  );

  const courseMessage = getAutocompleteSearchMessage(
    courseSearch.length,
    isCourseQuerySuccess,
    courseItems.length,
    {
      minChars: tCommon('search.minChars'),
      searching: tCommon('search.searching'),
      notFound: tCommon('search.notFound'),
    },
  );

  /* ── Active state ───────────────────────────────────────────────────────── */

  const hasActiveFilters =
    filters.statuses.length > 0 ||
    !!filters.company ||
    !!filters.polo ||
    filters.documentationFilter !== 'all' ||
    !!filters.courseId ||
    !!filters.studentName ||
    !!filters.cpf ||
    !!filters.documentStartDate ||
    !!filters.documentEndDate;

  /* ── Advanced active count ─────────────────────────────────────────────── */

  const advancedActiveCount =
    [
      filters.company,
      filters.polo,
      filters.cpf,
      filters.documentStartDate,
      filters.documentEndDate,
    ].filter(Boolean).length + (filters.documentationFilter !== 'all' ? 1 : 0);

  return (
    <div className="flex flex-col">
      {/* Row 1: busca + ação buscar + toggle avançado */}
      <div className="flex items-end justify-between gap-nano px-xs pt-xs pb-mini">
        <Autocomplete<string>
          label={t('enrollmentList.filters.studentName')}
          placeholder={t('enrollmentList.filters.searchPlaceholder')}
          size="md"
          list={studentItems}
          custom={{ root: 'w-full', groundedWrapper: 'w-full' }}
          {...(studentMessage !== undefined ? { notFoundMessage: studentMessage } : {})}
          selectedItem={selectedStudent ?? { label: '', value: '' }}
          onChange={e => setStudentSearch(e.target.value)}
          handleChange={item => {
            updateFilters({ studentName: item.value ?? '' });
            setSelectedStudentLabel(item.label ?? '');
            setStudentSearch('');
          }}
          handleClearQuery={() => {
            updateFilters({ studentName: '' });
            setSelectedStudentLabel('');
            setStudentSearch('');
          }}
        />

        <Button
          hierarchy="primary"
          size="md"
          leadingIcon={MagnifyingGlassIcon}
          disabled={!canApply || isLoading}
          isLoading={isLoading}
          onClick={applyFilters}
        >
          {t('enrollmentList.filters.search')}
        </Button>

        <div className="relative shrink-0">
          <Button
            hierarchy={showAdvanced ? 'primary' : 'secondary'}
            size="md"
            leadingIcon={AdjustmentsHorizontalIcon}
            trailingIcon={showAdvanced ? ChevronUpIcon : ChevronDownIcon}
            onClick={() => _setShowAdvanced(p => !p)}
          >
            {t('enrollmentList.filters.advancedFilters')}
          </Button>

          {advancedActiveCount > 0 && !showAdvanced && (
            <span className="absolute -right-[0.375rem] -top-[0.375rem] flex h-[1.125rem] w-[1.125rem] items-center justify-center rounded-pill bg-ctx-interactive-primary ts-caption-xxxs !font-medium text-base-neutral-white">
              {advancedActiveCount}
            </span>
          )}
        </div>
      </div>

      {/* Row 2: filtros dropdown + limpar */}
      <div className="flex flex-wrap items-end gap-nano px-xs pb-xs">
        <Autocomplete<string>
          label={t('enrollmentList.filters.course')}
          placeholder={t('enrollmentList.filters.coursePlaceholder')}
          size="md"
          list={courseItems}
          custom={{ input: 'md:!w-[28rem]' }}
          {...(courseMessage !== undefined ? { notFoundMessage: courseMessage } : {})}
          selectedItem={selectedCourse}
          onChange={e => setCourseSearch(e.target.value)}
          handleChange={item => {
            updateFilters({ courseId: item.value ?? '' });
            setSelectedCourseLabel(item.label ?? '');
            setCourseSearch('');
          }}
          handleClearQuery={() => {
            updateFilters({ courseId: '' });
            setSelectedCourseLabel('');
            setCourseSearch('');
          }}
        />

        <div className="w-[20rem]">
          <SelectInput
            label={t('enrollmentList.filters.status')}
            size="md"
            fullWidth
            value={filters.statuses[0] ?? 'all'}
            onValueChange={value =>
              updateFilters({
                statuses: value === 'all' ? [] : [value as EnrollmentStatus],
              })
            }
          >
            <SelectInputItem custom={{ root: 'text-nowrap' }} value="all">
              {t('enrollmentList.filters.all')}
            </SelectInputItem>
            {ALL_STATUSES.map(s => (
              <SelectInputItem custom={{ root: 'text-nowrap' }} key={s} value={s}>
                {t(`enrollmentStatus.${s}`)}
              </SelectInputItem>
            ))}
          </SelectInput>
        </div>

        {hasActiveFilters && (
          <button
            type="button"
            onClick={() => {
              resetFilters();
              setSelectedStudentLabel('');
              setDatePickerKey(k => k + 1);
            }}
            className="inline-flex items-center gap-quark rounded-pill border-hairline border-ctx-layout-border bg-ctx-layout-body px-xxs py-quark ts-caption-xxs text-ctx-content-placeholder transition-colors hover:bg-ctx-interactive-secondary hover:text-ctx-content-base"
          >
            <XMarkIcon className="h-[0.8125rem] w-[0.8125rem]" />
            {t('enrollmentList.filters.clear')}
          </button>
        )}
      </div>

      {/* Painel avançado */}
      {showAdvanced && (
        <div className="border-t-hairline border-ctx-layout-border">
          {/* Header do painel */}
          <div className="flex items-center justify-between bg-ctx-layout-body px-xs py-nano border-b-hairline border-ctx-layout-border">
            <div className="flex items-center gap-xxs">
              <AdjustmentsHorizontalIcon className="h-mini w-mini text-ctx-content-placeholder" />
              <span className="ts-caption-xxs !font-medium uppercase tracking-widest text-ctx-content-placeholder">
                {t('enrollmentList.filters.advancedFilters')}
              </span>
              {advancedActiveCount > 0 && (
                <span className="rounded-pill bg-ctx-interactive-primary px-xxs py-[0.0625rem] ts-caption-xxxs !font-medium text-base-neutral-white">
                  {advancedActiveCount} {t('enrollmentList.filters.active')}
                </span>
              )}
            </div>

            {advancedActiveCount > 0 && (
              <button
                type="button"
                onClick={() => {
                  updateFilters({
                    company: '',
                    polo: '',
                    cpf: '',
                    documentationFilter: 'all',
                    documentStartDate: '',
                    documentEndDate: '',
                  });
                  setDatePickerKey(k => k + 1);
                }}
                className="inline-flex items-center gap-quark ts-caption-xxs text-ctx-content-placeholder transition-colors hover:text-ctx-content-base"
              >
                <XMarkIcon className="h-[0.8125rem] w-[0.8125rem]" />
                {t('enrollmentList.filters.clearAdvanced')}
              </button>
            )}
          </div>

          {/* Campos */}
          <div className="grid grid-cols-1 gap-xs p-xs sm:grid-cols-2 lg:grid-cols-3">
            <Autocomplete<string>
              label={t('enrollmentList.filters.company')}
              placeholder={t('enrollmentList.filters.companyPlaceholder')}
              size="md"
              list={companyItems}
              {...(companyMessage !== undefined ? { notFoundMessage: companyMessage } : {})}
              selectedItem={selectedCompany}
              onChange={e => setCompanySearch(e.target.value)}
              handleChange={item => {
                updateFilters({ company: item.value ?? '' });
                setSelectedCompanyLabel(item.label ?? '');
                setCompanySearch('');
              }}
              handleClearQuery={() => {
                updateFilters({ company: '' });
                setSelectedCompanyLabel('');
                setCompanySearch('');
              }}
            />
            <SelectInput
              label={t('enrollmentList.filters.polo')}
              size="md"
              fullWidth
              value={filters.polo || 'all'}
              onValueChange={value => updateFilters({ polo: value === 'all' ? '' : value })}
            >
              <SelectInputItem custom={{ root: 'text-nowrap' }} value="all">
                {t('enrollmentList.filters.all')}
              </SelectInputItem>
              {polos.map(p => (
                <SelectInputItem custom={{ root: 'text-nowrap' }} key={p.id} value={p.id}>
                  {p.name}
                </SelectInputItem>
              ))}
            </SelectInput>
            <TextField
              label={t('enrollmentList.filters.cpf')}
              placeholder="000.000.000-00"
              size="md"
              value={filters.cpf}
              onChange={e => updateFilters({ cpf: e.target.value })}
            />
            <SelectInput
              label={t('enrollmentList.filters.documentation')}
              size="md"
              value={filters.documentationFilter}
              onValueChange={value =>
                updateFilters({
                  documentationFilter: value as 'all' | 'with_documents' | 'without_documents',
                })
              }
            >
              <SelectInputItem custom={{ root: 'text-nowrap' }} value="all">
                {t('enrollmentList.filters.all')}
              </SelectInputItem>
              <SelectInputItem custom={{ root: 'text-nowrap' }} value="with_documents">
                {t('enrollmentList.filters.withDocuments')}
              </SelectInputItem>
              <SelectInputItem custom={{ root: 'text-nowrap' }} value="without_documents">
                {t('enrollmentList.filters.withoutDocuments')}
              </SelectInputItem>
            </SelectInput>
            <DatePicker
              key={`start-${datePickerKey}`}
              label={t('enrollmentList.filters.docStartDate')}
              value={filters.documentStartDate}
              onChange={v => updateFilters({ documentStartDate: v })}
            />
            <DatePicker
              key={`end-${datePickerKey}`}
              label={t('enrollmentList.filters.docEndDate')}
              value={filters.documentEndDate}
              onChange={v => updateFilters({ documentEndDate: v })}
            />
          </div>
        </div>
      )}
    </div>
  );
}
